import React, {useEffect, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {useParams} from 'react-router-dom';
import type {CourseSessionTrackerResponse} from 'src/api/ProactApiClient';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {useGetCourseAssignmentActiveSessionQuery} from 'src/redux/queries/courseAssignment';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {CourseSessionContentTracker} from '../shared/courseSession/CourseSessionContentTracker';
import {TrainingDocumentTitle} from '../../../../app/training/courseContentValidation';

const CourseSessionTrackerPage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {assignmentId} = useParams();

  const {setAlert} = useAlert();

  const {
    data: initialTracker,
    isLoading,
    error,
  } = useGetCourseAssignmentActiveSessionQuery(parseInt(assignmentId ?? '0'), {
    skip: !assignmentId,
    refetchOnMountOrArgChange: true,
  });

  const [tracker, setTracker] = useState<
    CourseSessionTrackerResponse | undefined
  >(undefined);

  const [isOverlayHeld, setIsOverlayHeld] = useState(false);

  useEffect(() => {
    if (initialTracker && !isOverlayHeld) {
      setTracker(initialTracker);
    }
  }, [initialTracker, isOverlayHeld]);

  useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error]);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'user-session-tracker',
    courseName: tracker?.courseDefinition.name,
    assignmentId: tracker ? String(tracker.assignment.id) : undefined,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {isLoading ? (
          <Spinner loadingText="Loading...." />
        ) : error || !tracker ? (
          <Alert color="red" title="Error">
            Failed to load course session. Please try again later.
          </Alert>
        ) : (
          <CourseSessionContentTracker
            tracker={tracker}
            onSetTracker={setTracker}
            onHoldChange={setIsOverlayHeld}
          />
        )}
      </Box>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingSessionTracker(':assignmentId'),
  element: <CourseSessionTrackerPage />,
  isPrivate: true,
});

export default CourseSessionTrackerPage;
